import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import AppContext from '../../../Context/AppContext';
import configData from '../../../config.json';
import ModalErrorV2 from '../Modals/ModalErrorV2';
import { readableFColor } from '../../../util/readableBrandColor';
import './EstablishActionsV2.css';

const DEFAULT_HINTS = [
    'Walk for 20 minutes after lunch',
    'Call the person I need to talk to',
    'Spend 15 minutes reviewing my goal',
    'Schedule one appointment',
    'Complete one small step today',
];

const PAGE_SIZE = 5;
const MAX_ACTIONS = 15;
const MIN_ACTIONS = 3;

function resolvePlaceholder(hints, index) {
    const fromFlow = hints?.[index];
    if (fromFlow != null && String(fromFlow) !== '') {
        return String(fromFlow);
    }
    if (DEFAULT_HINTS[index] != null) {
        return DEFAULT_HINTS[index];
    }
    return 'Write your action step ';
}

function slotCapacityHint(count, atMax) {
    if (atMax) {
        return `${MAX_ACTIONS} of ${MAX_ACTIONS} slots used`;
    }
    if (count <= 0) {
        return `${MAX_ACTIONS} slots available today`;
    }
    return `${count} of ${MAX_ACTIONS} slots used`;
}

function dockProgressLabel(ready, readyCount, filledCount) {
    if (!ready) {
        return `${readyCount} / ${MIN_ACTIONS} actions ready`;
    }
    if (filledCount > MIN_ACTIONS) {
        return `${filledCount} / ${MAX_ACTIONS} actions added`;
    }
    return `${MIN_ACTIONS} / ${MIN_ACTIONS} actions ready`;
}

/**
 * Portal V2 — Establish 3+ daily actions against Top 7 goals.
 * Classic EstablishActionSection stays on /steps.
 */
export default function EstablishActionsV2({
    AllState,
    updateIndexStep3,
    mobile,
}) {
    const { checkUserSession, changeLoading } = useContext(AppContext);
    const { token, userId } = checkUserSession() || {};
    const goals = (AllState?.My7 || []).slice(0, 7);
    const actions = AllState?.Action || [];
    const hints = AllState?.ActionPlaceholders || DEFAULT_HINTS;
    const filled = actions.filter((x) => (x?.text || '').trim() !== '');
    const filledCount = filled.length;
    const readyCount = Math.min(filledCount, MIN_ACTIONS);
    const progressPct = Math.round((readyCount / MIN_ACTIONS) * 100);
    const ready = filledCount >= MIN_ACTIONS;
    const atMax = actions.length >= MAX_ACTIONS;

    const pageCount = Math.max(1, Math.ceil(actions.length / PAGE_SIZE));
    const [page, setPage] = useState(0);
    const [showTops, setShowTops] = useState(!mobile);
    const [errorOpen, setErrorOpen] = useState(false);
    const [errorTitle, setErrorTitle] = useState('');
    const [errorDesc, setErrorDesc] = useState('');
    const [focusGoal, setFocusGoal] = useState(0);
    const [slideDir, setSlideDir] = useState(0);
    const swipeRef = useRef({ x: null, y: null });
    const swipeElRef = useRef(null);

    const canSwipePages = pageCount > 1;
    const slotsHint = slotCapacityHint(actions.length, atMax);
    const dockMeta = dockProgressLabel(ready, readyCount, filledCount);
    const dockCtaLabel = ready
        ? 'Continue →'
        : 'Complete 3 actions to continue';

    useEffect(() => {
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;
    }, []);

    useEffect(() => {
        setShowTops(!mobile);
    }, [mobile]);

    useEffect(() => {
        if (page > pageCount - 1) {
            setPage(Math.max(0, pageCount - 1));
        }
    }, [page, pageCount]);

    const pageStart = page * PAGE_SIZE;
    const pageActions = useMemo(
        () =>
            actions
                .map((item, index) => ({ item, index }))
                .slice(pageStart, pageStart + PAGE_SIZE),
        [actions, pageStart]
    );

    const persist = (next) => {
        const apply =
            typeof AllState?.changeData === 'function'
                ? AllState.changeData
                : null;
        if (!apply) return;
        apply({
            ...AllState,
            ...next,
            changeData: apply,
        });
    };

    const setActionText = (index, value) => {
        const cleaned = String(value || '')
            .replace(/^\s+/, '')
            .replace(/  +/g, ' ');
        if (cleaned.length > 300) return;
        const nextActions = (actions || []).map((a, i) =>
            i === index ? { ...a, text: cleaned } : a
        );
        persist({ Action: nextActions });
    };

    const addAction = () => {
        if (atMax) {
            setErrorTitle('You are almost there');
            setErrorDesc(`You can add only ${MAX_ACTIONS} actions.`);
            setErrorOpen(true);
            return;
        }
        const next = [...actions, { text: '' }];
        persist({ Action: next });
        setPage(Math.floor((next.length - 1) / PAGE_SIZE));
    };

    const saveActions = () => {
        if (!ready) {
            setErrorTitle('Add a few more');
            setErrorDesc(
                'Define at least 3 action steps you will take in the next 24 hours.'
            );
            setErrorOpen(true);
            return;
        }
        changeLoading?.(true);
        axios({
            method: 'post',
            url: `${configData.SERVER_URL}Actions`,
            headers: { Authorization: `Bearer ${token}` },
            data: { userId, actions: filled },
        })
            .then(() => {
                changeLoading?.(false);
                updateIndexStep3?.(1);
                window.scrollTo(0, 0);
            })
            .catch((err) => {
                changeLoading?.(false);
                setErrorTitle('Could not save actions');
                setErrorDesc(
                    err?.message ||
                        'Something went wrong saving your actions. Please try again.'
                );
                setErrorOpen(true);
            });
    };

    const goPage = (dir) => {
        if (!dir) return;
        setSlideDir(dir);
        setPage((p) => Math.min(pageCount - 1, Math.max(0, p + dir)));
    };

    useEffect(() => {
        const el = swipeElRef.current;
        if (!el || pageCount <= 1) return undefined;

        const onStart = (event) => {
            const touch = event.changedTouches?.[0];
            if (!touch) return;
            const onControl = Boolean(
                event.target?.closest?.(
                    'button, a, .pv2-act3__nav-btn, .pv2-act3__dot'
                )
            );
            swipeRef.current = {
                x: onControl ? null : touch.clientX,
                y: onControl ? null : touch.clientY,
            };
        };

        const onMove = (event) => {
            const start = swipeRef.current;
            const touch = event.touches?.[0];
            if (start.x == null || start.y == null || !touch) return;
            const dx = touch.clientX - start.x;
            const dy = touch.clientY - start.y;
            if (Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy)) {
                event.preventDefault();
            }
        };

        const onEnd = (event) => {
            const start = swipeRef.current;
            swipeRef.current = { x: null, y: null };
            const touch = event.changedTouches?.[0];
            if (start.x == null || start.y == null || !touch) return;
            const dx = touch.clientX - start.x;
            const dy = touch.clientY - start.y;
            if (Math.abs(dx) < 40) return;
            if (Math.abs(dx) < Math.abs(dy) * 1.05) return;
            if (document.activeElement?.blur) {
                document.activeElement.blur();
            }
            if (dx < 0) goPage(1);
            else goPage(-1);
        };

        el.addEventListener('touchstart', onStart, { passive: true });
        el.addEventListener('touchmove', onMove, { passive: false });
        el.addEventListener('touchend', onEnd, { passive: true });
        return () => {
            el.removeEventListener('touchstart', onStart);
            el.removeEventListener('touchmove', onMove);
            el.removeEventListener('touchend', onEnd);
        };
    }, [pageCount]);

    return (
        <section className="pv2-act3">
            <div className="pv2-act3__card">
                <aside className={`pv2-act3__side${showTops ? ' is-open' : ''}`}>
                    <header className="pv2-act3__side-head">
                        <div>
                            <p className="pv2-act3__eyebrow pv2-act3__eyebrow--desk">
                                Your goals
                            </p>
                            <h2 className="pv2-act3__side-title">My Top 7</h2>
                            <p className="pv2-act3__side-lead">
                                Context for every action you write
                            </p>
                            {mobile && !showTops ? (
                                <p className="pv2-act3__side-summary">
                                    {goals.length} goals selected
                                </p>
                            ) : null}
                        </div>
                        {mobile ? (
                            <button
                                type="button"
                                className="pv2-act3__toggle"
                                onClick={() => setShowTops((v) => !v)}
                                aria-expanded={showTops}
                            >
                                {showTops ? 'Hide' : 'Show'}
                            </button>
                        ) : null}
                    </header>

                    {showTops ? (
                        <ul className="pv2-act3__goals">
                            {goals.map((goal, index) => {
                                const accent = readableFColor(
                                    goal.name,
                                    goal.color || '#15c285'
                                );
                                const isFocus = focusGoal === index;
                                return (
                                    <li
                                        key={goal.id || `g-${index}`}
                                        className={`pv2-act3__goal${
                                            isFocus ? ' is-focus' : ''
                                        }${index === 0 ? ' is-oola' : ''}`}
                                        style={{
                                            ['--pv2-act-accent']: accent,
                                            ['--pv2-act-stagger']: `${
                                                index * 45
                                            }ms`,
                                        }}
                                        onMouseEnter={() => setFocusGoal(index)}
                                        onFocus={() => setFocusGoal(index)}
                                    >
                                        <span
                                            className="pv2-act3__goal-num"
                                            aria-hidden="true"
                                        >
                                            {index + 1}
                                        </span>
                                        <div className="pv2-act3__goal-body">
                                            <span className="pv2-act3__goal-cat">
                                                {goal.name || 'Goal'}
                                            </span>
                                            <span
                                                className="pv2-act3__goal-text"
                                                title={goal.text || ''}
                                            >
                                                {goal.text}
                                            </span>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    ) : null}
                </aside>

                <div className="pv2-act3__main">
                    <header className="pv2-act3__main-head">
                        <p className="pv2-act3__eyebrow">Step 3 · Actions</p>
                        <h2 className="pv2-act3__title">
                            Create today’s action plan
                        </h2>
                        <p className="pv2-act3__lead">
                            <span className="pv2-act3__copy-desk">
                                Write 3+ actions you will take in the next 24
                                hours toward your Top 7.
                            </span>
                            <span className="pv2-act3__copy-mob">
                                Write 3 actions you can complete in the next 24
                                hours.
                            </span>
                        </p>

                        <div
                            className={`pv2-act3__progress${
                                ready ? ' is-ready' : ''
                            }`}
                            aria-live="polite"
                        >
                            <div className="pv2-act3__progress-top">
                                <span className="pv2-act3__count">
                                    <strong key={readyCount}>{readyCount}</strong>
                                    <span> / {MIN_ACTIONS} actions ready</span>
                                </span>
                                <div
                                    className="pv2-act3__ready-dots"
                                    aria-hidden="true"
                                >
                                    {[0, 1, 2].map((i) => (
                                        <span
                                            key={`rd-${i}`}
                                            className={`pv2-act3__ready-dot${
                                                i < readyCount ? ' is-on' : ''
                                            }`}
                                        />
                                    ))}
                                </div>
                            </div>
                            <div
                                className="pv2-act3__bar"
                                role="progressbar"
                                aria-valuemin={0}
                                aria-valuemax={MIN_ACTIONS}
                                aria-valuenow={readyCount}
                                aria-label="Actions ready"
                            >
                                <span
                                    className="pv2-act3__bar-fill"
                                    style={{ width: `${progressPct}%` }}
                                />
                            </div>
                        </div>
                        <p className="pv2-act3__nudge">
                            <span className="pv2-act3__copy-desk">
                                Start with three simple actions you can complete
                                today.
                            </span>
                            <span className="pv2-act3__copy-mob">
                                You can add up to 15 actions today.
                            </span>
                        </p>
                    </header>

                    <div
                        ref={swipeElRef}
                        className={`pv2-act3__swipe${
                            canSwipePages ? ' is-swipeable' : ''
                        }`}
                        style={
                            canSwipePages && slideDir
                                ? {
                                      ['--pv2-act3-slide']:
                                          slideDir > 0 ? '12px' : '-12px',
                                  }
                                : undefined
                        }
                    >
                    <ul
                        key={canSwipePages ? `page-${page}` : 'page-static'}
                        className={`pv2-act3__actions${
                            canSwipePages ? ' is-paged' : ''
                        }`}
                    >
                        {pageActions.map(({ item, index }, rowIdx) => {
                            const hasText = (item?.text || '').trim() !== '';
                            const placeholder = resolvePlaceholder(
                                hints,
                                index
                            );
                            return (
                                <li
                                    key={`action-${index}`}
                                    style={{
                                        ['--pv2-act-row-stagger']: `${
                                            rowIdx * 40
                                        }ms`,
                                    }}
                                >
                                    <label
                                        className={`pv2-act3__row${
                                            hasText ? ' is-filled' : ''
                                        }`}
                                    >
                                        <span
                                            className="pv2-act3__num"
                                            aria-hidden="true"
                                        >
                                            {hasText ? (
                                                <svg
                                                    className="pv2-act3__check"
                                                    viewBox="0 0 16 16"
                                                    width="14"
                                                    height="14"
                                                    focusable="false"
                                                >
                                                    <path
                                                        d="M3.2 8.2 6.4 11.4 12.8 4.6"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        strokeWidth="2"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                </svg>
                                            ) : (
                                                index + 1
                                            )}
                                        </span>
                                        <span className="pv2-act3__field">
                                            {!hasText ? (
                                                <span
                                                    className="pv2-act3__ph"
                                                    title={placeholder}
                                                    aria-hidden="true"
                                                >
                                                    {mobile
                                                        ? `e.g. ${placeholder}`
                                                        : placeholder}
                                                </span>
                                            ) : null}
                                            <textarea
                                                className="pv2-act3__input"
                                                rows={1}
                                                placeholder=""
                                                aria-label={
                                                    placeholder ||
                                                    `Action step ${index + 1}`
                                                }
                                                title={placeholder}
                                                value={item?.text || ''}
                                                onChange={(e) =>
                                                    setActionText(
                                                        index,
                                                        e.target.value
                                                    )
                                                }
                                                maxLength={300}
                                            />
                                        </span>
                                    </label>
                                </li>
                            );
                        })}
                    </ul>

                    <div
                        className={`pv2-act3__pager${
                            pageCount > 1 ? '' : ' is-hidden'
                        }`}
                        aria-hidden={pageCount <= 1}
                    >
                        <button
                            type="button"
                            className="pv2-act3__nav-btn"
                            onClick={() => goPage(-1)}
                            aria-label="Previous page"
                            disabled={page <= 0 || pageCount <= 1}
                        >
                            ‹
                        </button>
                        <div className="pv2-act3__pager-mid">
                            <div
                                className="pv2-act3__dots"
                                role="tablist"
                                aria-label="Action pages"
                            >
                                {Array.from({ length: pageCount }).map(
                                    (_, i) => {
                                        const start = i * PAGE_SIZE;
                                        const slice = actions.slice(
                                            start,
                                            start + PAGE_SIZE
                                        );
                                        const pageFilled = slice.some(
                                            (a) =>
                                                (a?.text || '').trim() !== ''
                                        );
                                        return (
                                            <button
                                                key={`page-${i}`}
                                                type="button"
                                                role="tab"
                                                aria-selected={i === page}
                                                className={`pv2-act3__dot${
                                                    i === page ? ' is-on' : ''
                                                }${
                                                    pageFilled
                                                        ? ' is-filled'
                                                        : ''
                                                }`}
                                                onClick={() => {
                                                    if (i === page) return;
                                                    setSlideDir(i > page ? 1 : -1);
                                                    setPage(i);
                                                }}
                                                aria-label={`Page ${i + 1}`}
                                                tabIndex={
                                                    pageCount <= 1 ? -1 : 0
                                                }
                                            />
                                        );
                                    }
                                )}
                            </div>
                            <p className="pv2-act3__page-label">
                                {page + 1} of {pageCount}
                            </p>
                        </div>
                        <button
                            type="button"
                            className="pv2-act3__nav-btn"
                            onClick={() => goPage(1)}
                            aria-label="Next page"
                            disabled={page >= pageCount - 1 || pageCount <= 1}
                        >
                            ›
                        </button>
                    </div>
                    </div>

                    <div className="pv2-act3__toolbar pv2-act3__toolbar--desk">
                        <div className="pv2-act3__toolbar-left">
                            <button
                                type="button"
                                className={`pv2-act3__add${
                                    atMax ? ' is-max' : ''
                                }${mobile && !ready ? ' is-quiet' : ''}`}
                                onClick={addAction}
                                disabled={atMax}
                                aria-disabled={atMax}
                            >
                                {mobile
                                    ? '+ Add more'
                                    : atMax
                                      ? `${MAX_ACTIONS} of ${MAX_ACTIONS} slots max`
                                      : `+ Add more (${actions.length} of ${MAX_ACTIONS} slots)`}
                            </button>
                            {mobile && ready ? (
                                <p className="pv2-act3__slots-hint">{slotsHint}</p>
                            ) : null}
                        </div>
                        <div className="pv2-act3__toolbar-right pv2-act3__toolbar-right--desk">
                            {!ready ? (
                                <p className="pv2-act3__cta-hint">
                                    Complete 3 action steps to continue.
                                </p>
                            ) : (
                                <p className="pv2-act3__cta-hint is-ready">
                                    Your plan is ready — keep going.
                                </p>
                            )}
                            <button
                                type="button"
                                className={`pv2-act3__cta${
                                    ready ? ' is-ready' : ' is-disabled'
                                }`}
                                disabled={!ready}
                                onClick={saveActions}
                            >
                                Continue
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {mobile && typeof document !== 'undefined'
                ? createPortal(
                      <div
                          className={`pv2-act3__dock${
                              ready ? ' is-ready' : ''
                          }`}
                          aria-label="Action plan progress"
                      >
                          <div className="pv2-act3__dock-add">
                              <button
                                  type="button"
                                  className={`pv2-act3__add pv2-act3__dock-add-btn${
                                      atMax ? ' is-max' : ''
                                  }${ready ? '' : ' is-quiet'}`}
                                  onClick={addAction}
                                  disabled={atMax}
                                  aria-disabled={atMax}
                              >
                                  + Add more
                              </button>
                              <p className="pv2-act3__slots-hint">{slotsHint}</p>
                          </div>
                          <div className="pv2-act3__dock-row">
                              <div className="pv2-act3__dock-meta">
                                  <span>{dockMeta}</span>
                              </div>
                              <button
                                  type="button"
                                  className={`pv2-act3__dock-cta${
                                      ready ? ' is-ready' : ' is-disabled'
                                  }`}
                                  disabled={!ready}
                                  aria-disabled={!ready}
                                  onClick={saveActions}
                              >
                                  {dockCtaLabel}
                              </button>
                          </div>
                      </div>,
                      document.body
                  )
                : null}

            <ModalErrorV2
                show={errorOpen}
                setShowError={setErrorOpen}
                ModalErrorTitle={errorTitle}
                ModalErrorDesc={errorDesc}
            />
        </section>
    );
}
